import { Controller, FieldErrors, Path, useFormContext } from "react-hook-form";
import { AddressValidationSchema } from "@/common/schemas/address.schema";
import AddressMapImage from "../AddressMapImage";
import { UseAddressForm } from "@/common/hooks/useAddressForm";
import { CityTextField, ComplementTextField, NeighborhoodTextField, NumberTextField, StateTextField, StreetTextField, ZipcodeTextField } from "./components/AddressFormFields";
import AddressFormLoading from "./components/AddressFormLoading";

export interface AddressFormProps<T extends {}> {
  addressForm: UseAddressForm<T>;
  disableFields?: boolean;
  errors?: FieldErrors<AddressValidationSchema>;
  markFieldsAsRequired?: boolean;
  componentBelowMap?: React.ReactNode;
}

const AddressForm = <T extends {}>({
  addressForm,
  disableFields,
  errors,
  markFieldsAsRequired,
  componentBelowMap,
}: AddressFormProps<T>) => {
  const {
    path,
    address,
    zipcode,
    street,
    number,
    city,
    neighborhood,
    complement,
    setZipcode,
    setStreet,
    setNumber,
    setNeighborhood,
    setComplement,
    zipcodeDataIsPending,
    zipcodeError,
    isLoading,
    onZipcodeChange,
  } = addressForm;

  const formMethods = useFormContext();

  const controlledByHookForm = formMethods && path;

  if (isLoading) {
    return <AddressFormLoading />;
  }

  return (
    <div className="flex gap-6">
      <div className="w-1/2 flex flex-col gap-4">
        {controlledByHookForm ? (
          <Controller
            control={formMethods.control}
            name={`${path}.zipcode` as Path<T>}
            render={({ field }) => (
              <ZipcodeTextField
                value={field.value}
                error={zipcodeError || formMethods.formState?.errors?.zipcode?.message as string | undefined || errors?.zipcode?.message}
                onChange={(value) => {
                  onZipcodeChange();
                  setZipcode(value);
                  field.onChange({ target: { value } });
                }}
                disabled={disableFields}
                markAsRequired={markFieldsAsRequired}
                isPending={zipcodeDataIsPending}
              />
            )}
          />
        ) : (
          <ZipcodeTextField
            value={zipcode}
            error={zipcodeError || errors?.zipcode?.message}
            onChange={(value: string) => {
              onZipcodeChange();
              setZipcode(value);
            }}
            disabled={disableFields}
            markAsRequired={markFieldsAsRequired}
            isPending={zipcodeDataIsPending}
          />
        )}


        <div className="flex gap-2">
          {controlledByHookForm ? (
            <Controller
              control={formMethods.control}
              name={`${path}.street` as Path<T>}
              render={({ field }) => (
                <StreetTextField
                  value={field.value}
                  error={formMethods.formState?.errors?.street?.message as string | undefined || errors?.street?.message}
                  onChange={(value) => {
                    setStreet(value);
                    field.onChange({ target: { value } });
                  }}
                  disabled={disableFields}
                  markAsRequired={markFieldsAsRequired}
                />
              )}
            />
          ) : (
            <StreetTextField
              value={street}
              error={errors?.street?.message}
              onChange={setStreet}
              disabled={disableFields}
              markAsRequired={markFieldsAsRequired}
            />
          )}

          {controlledByHookForm ? (
            <Controller
              control={formMethods.control}
              name={`${path}.number` as Path<T>}
              render={({ field }) => (
                <NumberTextField
                  value={field.value || ''}
                  error={formMethods.formState?.errors?.number?.message as string | undefined || errors?.number?.message}
                  onChange={(value) => {
                    setNumber(value);
                    field.onChange({ target: { value } });
                  }}
                  disabled={disableFields}
                  markAsRequired={markFieldsAsRequired}
                />
              )}
            />
          ) : (
            <NumberTextField
              value={number || ''}
              error={errors?.number?.message}
              onChange={setNumber}
              disabled={disableFields}
              markAsRequired={markFieldsAsRequired}
            />
          )}
        </div>

        {controlledByHookForm ? (
          <CityTextField
            value={formMethods?.watch(`${path}.city.display_name`) || ''}
            error={formMethods.formState?.errors?.city_id?.message as string | undefined || errors?.city?.message}
            markAsRequired={markFieldsAsRequired}
          />
        ) : (
          <CityTextField
            value={city?.display_name || ''}
            error={errors?.city_id?.message}
            markAsRequired={markFieldsAsRequired}
          />
        )}

        {controlledByHookForm ? (
          <Controller
            control={formMethods.control}
            name={`${path}.neighborhood` as Path<T>}
            render={({ field }) => (
              <NeighborhoodTextField
                value={field.value}
                error={formMethods.formState?.errors?.neighborhood?.message as string | undefined || errors?.neighborhood?.message}
                onChange={(value) => {
                  setNeighborhood(value);
                  field.onChange({ target: { value } });
                }}
                disabled={disableFields}
                markAsRequired={markFieldsAsRequired}
              />
            )}
          />
        ) : (
          <NeighborhoodTextField
            value={neighborhood}
            error={errors?.neighborhood?.message}
            onChange={setNeighborhood}
            disabled={disableFields}
            markAsRequired={markFieldsAsRequired}
          />
        )}

        {controlledByHookForm ? (
          <StateTextField
            value={formMethods?.watch(`${path}.city.state.display_name`) || ''}
            markAsRequired={markFieldsAsRequired}
          />
        ) : (
          <StateTextField
            value={city?.state?.display_name || ''}
            markAsRequired={markFieldsAsRequired}
          />
        )}

        {controlledByHookForm ? (
          <Controller
            control={formMethods.control}
            name={`${path}.complement` as Path<T>}
            render={({ field }) => (
              <ComplementTextField
                value={field.value}
                onChange={(value) => {
                  setComplement(value);
                  field.onChange({ target: { value } });
                }}
                disabled={disableFields}
              />
            )}
          />
        ) : (
          <ComplementTextField
            value={complement}
            onChange={setComplement}
            disabled={disableFields}
          />
        )}
      </div>
      <div className="flex flex-col gap-6 w-1/2">
        <AddressMapImage address={address} />

        {componentBelowMap}
      </div>
    </div>
  );
};

export default AddressForm;
